import type { Placement } from '@floating-ui/react';
import type { LinkProps } from 'next/link';
import type { ReactNode } from 'react';
import Link from 'next/link';
import { cn } from '@poseidon/components/cn';
import React from 'react';

import type { TooltipLabel } from '@poseidon/components/Tooltip';
import { Tooltip } from '@poseidon/components/Tooltip';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import type { InputSize } from '~/components/Input/types';
import styles from './Button.module.scss';

export type ButtonType = 'primary' | 'dangerous' | 'success' | 'submit' | 'submitPrimary' | 'text' | 'gradient';
export type BorderType = 'solid' | 'dashed';

type CommonProps = {
  icon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  type?: ButtonType;
  isLoading?: boolean;
  tooltip?: TooltipLabel;
  tooltipPlacement?: Placement;
  size?: InputSize;
};

type ButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, 'size' | 'type'> &
  CommonProps & {
    hoverable?: boolean;
    borderType?: BorderType;
    align?: 'left' | 'right';
    animating?: boolean;
    contentClassName?: string;
  };

type LinkButtonProps = Omit<LinkProps, 'size' | 'type'> &
  CommonProps & {
    className?: string;
    children?: ReactNode;
    target?: string;
    onMouseLeave?: React.MouseEventHandler<HTMLAnchorElement>;
  };

export const Button = ({
  icon,
  rightIcon,
  children,
  type,
  className,
  tooltip,
  onClick,
  disabled,
  size,
  hoverable,
  tooltipPlacement,
  animating,
  isLoading,
  borderType = 'solid',
  contentClassName,
  align,
  ref,
  ...restProps
}: ButtonProps) => {
  const isSubmitButton = type === 'submit' || type === 'submitPrimary';
  const classList = cn(
    styles.button,
    size ? styles[size] : undefined,
    type ? styles[type] : undefined,
    align ? styles[align] : undefined,
    borderType ? styles[borderType] : undefined,
    type === 'submitPrimary' ? styles.primary : undefined,
    (onClick || isSubmitButton || hoverable) && !disabled ? styles.hoverable : styles.disabled,
    animating ? styles.animating : undefined,
    className,
  );

  if (tooltip) {
    return (
      <Tooltip
        label={tooltip}
        background={type === 'dangerous' ? 'hsl(0, 67%, 45%)' : undefined}
        placement={tooltipPlacement}
        autoPlace={tooltipPlacement != null ? false : true}>
        <button
          className={classList}
          onClick={onClick}
          type={isSubmitButton ? 'submit' : 'button'}
          ref={ref}
          disabled={isLoading || disabled}
          {...restProps}>
          {isLoading && (
            <div className={styles.icon}>
              <WaveSpinner />
            </div>
          )}
          {icon != null && !isLoading && <div className={styles.icon}>{icon}</div>}
          {children != null && <span className={contentClassName}>{children}</span>}
          {rightIcon != null && <div className={styles.icon}>{rightIcon}</div>}
        </button>
      </Tooltip>
    );
  }

  return (
    <button
      className={classList}
      onClick={onClick}
      type={isSubmitButton ? 'submit' : 'button'}
      ref={ref}
      disabled={isLoading || disabled}
      {...restProps}>
      {isLoading && (
        <div className={styles.icon}>
          <WaveSpinner />
        </div>
      )}
      {icon != null && !isLoading && <div className={styles.icon}>{icon}</div>}
      {children != null && <span className={contentClassName}>{children}</span>}
      {rightIcon != null && <div className={styles.icon}>{rightIcon}</div>}
    </button>
  );
};

export const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    { icon, children, type, className, href, target, size, tooltip, rightIcon, isLoading, tooltipPlacement, ...props },
    ref,
  ) => {
    const classList = cn(
      styles.button,
      size ? styles[size] : undefined,
      type ? styles[type] : undefined,
      styles.hoverable,
      className,
    );

    if (tooltip) {
      return (
        <Tooltip label={tooltip} placement={tooltipPlacement} autoPlace={tooltipPlacement != null ? false : true}>
          <Link className={classList} href={href} target={target} ref={ref} {...props}>
            {isLoading && (
              <div className={styles.icon}>
                <WaveSpinner />
              </div>
            )}
            {icon != null && <div className={styles.icon}>{icon}</div>}
            {children != null && <span>{children}</span>}
            {rightIcon != null && <div className={styles.icon}>{rightIcon}</div>}
          </Link>
        </Tooltip>
      );
    }

    return (
      <Link className={classList} href={href} target={target} ref={ref} {...props}>
        {isLoading && (
          <div className={styles.icon}>
            <WaveSpinner />
          </div>
        )}
        {icon != null && <div className={styles.icon}>{icon}</div>}
        {children != null && <span>{children}</span>}
        {rightIcon != null && <div className={styles.icon}>{rightIcon}</div>}
      </Link>
    );
  },
);
LinkButton.displayName = 'LinkButton';

type ButtonsGroupProps = Omit<React.HTMLProps<HTMLDivElement>, 'size'> & {
  size?: InputSize;
};
export const ButtonsGroup = ({ className, size, children, ...restProps }: ButtonsGroupProps) => {
  const validButtons = React.Children.toArray(children)?.filter(React.isValidElement<ButtonProps>);

  return (
    <div {...restProps} className={cn(styles.group, size == 'small' ? styles.small : undefined, className)}>
      {validButtons.map((child, index) => (
        <React.Fragment key={index}>
          {index != 0 && <div className={styles.groupDivider} />}
          {React.cloneElement(child, { size })}
        </React.Fragment>
      ))}
    </div>
  );
};

export type OrgButtonProps<TID> = Omit<React.HTMLProps<HTMLDivElement>, 'size'> & {
  icon?: React.JSX.Element;
  uid: TID;
  activeUid?: TID;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
  size?: InputSize;
  tooltip?: TooltipLabel;
  tooltipPlacement?: Placement;
};

export function OrgButton<TID>({
  icon,
  activeUid,
  uid,
  className,
  onClick,
  size,
  tooltip,
  tooltipPlacement,
}: OrgButtonProps<TID>) {
  const isActive = uid === activeUid;
  const classList = cn(
    className,
    size == 'small' ? styles.small : undefined,
    isActive ? styles.active : undefined,
    styles.orgButton,
    styles.hoverable,
  );

  if (tooltip) {
    return (
      <Tooltip label={tooltip} placement={tooltipPlacement} autoPlace={tooltipPlacement != null ? false : true}>
        <div className={classList} onClick={onClick}>
          {icon != null && <span className={styles.icon}>{icon}</span>}
        </div>
      </Tooltip>
    );
  }

  return (
    <div className={classList} onClick={onClick}>
      {icon != null && <span className={styles.icon}>{icon}</span>}
    </div>
  );
}
